import { css } from 'aphrodite';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { IAICampaignGenerationJob, TERMINAL_AI_CAMPAIGN_STATUSES } from '../../../../models/AICampaign';
import { useAICampaignGenerationJob } from '../../../../queries/AICampaign';
import { styleSheet } from './styles';

interface IProps {
	jobId: string;
	onComplete?: (job: IAICampaignGenerationJob) => void;
}

export const AICampaignGenerationProgress: React.FC<IProps> = ({ jobId, onComplete }) => {
	const { data: job } = useAICampaignGenerationJob({ jobId });
	const isTerminal = !!job?.status && TERMINAL_AI_CAMPAIGN_STATUSES.includes(job.status);

	const onCompleteRef = useRef(onComplete);
	onCompleteRef.current = onComplete;
	// Keyed by job, so a retry that ends in the same terminal status as the run before it still reports.
	const handledRef = useRef<string | null>(null);

	useEffect(() => {
		if (!job || !TERMINAL_AI_CAMPAIGN_STATUSES.includes(job.status)) {
			return;
		}

		const handled = `${jobId}:${job.status}`;
		if (handledRef.current === handled) {
			return;
		}
		handledRef.current = handled;

		if (job.status === 'Complete') {
			toast.success(`Wrote ${job.generatedCount.toLocaleString()} emails`);
		} else if (job.status === 'PartialSuccess') {
			toast.info(
				`Wrote ${job.generatedCount.toLocaleString()} emails, ${job.failedCount.toLocaleString()} could not be written`
			);
		} else {
			toast.error(job.errorMessage || 'Generation failed and no emails were written');
		}

		onCompleteRef.current?.(job);
	}, [job, jobId]);

	if (!job) {
		return null;
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<p className={css(styleSheet.status)}>{getStatusLabel(job)}</p>
				<span className={css(styleSheet.percent)}>{job.percentComplete}%</span>
			</div>

			<div className={css(styleSheet.progressTrack)}>
				<div className={css(styleSheet.progressFill)} style={{ width: `${job.percentComplete}%` }} />
			</div>

			{!isTerminal && !!job.currentContactName && (
				<p className={css(styleSheet.currentContact)}>{`Writing for ${job.currentContactName}`}</p>
			)}

			<p className={css(styleSheet.counts)}>
				<span>{`${job.generatedCount.toLocaleString()} written`}</span>
				{job.skippedCount > 0 && <span>{`${job.skippedCount.toLocaleString()} skipped`}</span>}
				{job.failedCount > 0 && <span>{`${job.failedCount.toLocaleString()} failed`}</span>}
				<span>{`${job.processedContacts.toLocaleString()} of ${job.totalContacts.toLocaleString()} recipients`}</span>
			</p>

			{job.status === 'Failed' && !!job.errorMessage && <p className={css(styleSheet.error)}>{job.errorMessage}</p>}
		</div>
	);
};

const getStatusLabel = (job: IAICampaignGenerationJob) => {
	switch (job.status) {
		case 'Queued':
			return 'Getting ready...';
		case 'InProgress':
			return job.webSearchEnabled ? 'Researching and writing emails...' : 'Writing emails...';
		case 'Complete':
			return 'All emails written';
		case 'PartialSuccess':
			return 'Finished with some emails not written';
		default:
			return 'Generation failed';
	}
};
